/**
 * Transaction Service Implementation
 * Handles transaction update operations
 */

import type { AxiosInstance } from "axios";
import type { RequestConfig } from "../types/common";
import type { CashInLocation, ServiceOptionCode } from "../types/moneygram";

// Transaction status response type
export interface TransactionStatusResponse {
	isRefundAllowedFlag: boolean;
	receiverMiddleName: string;
	receiverSecondLastName: string;
	cashInWalletFeeEnabled: boolean;
	cashOutWalletFeeEnabled: boolean;
	payoutOptionsCode?: ServiceOptionCode; // Optional - may be missing in v3
	transactionId: string;
	referenceNumber?: string; // Optional - deposit uses confirmationNumber instead
	confirmationCode?: string; // For deposit flow (legacy)
	confirmationNumber?: string; // For deposit flow (v3 uses this)
	transactionStatus: string;
	substatus?: string[];
	startTime: string;
	receiverFirstName: string;
	receiverLastName: string;
	walletProvider: string;
	expectedPayoutDate?: string;
	selectedLocation?: CashInLocation; // Location info for deposit flow (direct)
	customFields: Array<{
		key: string;
		value: string;
	}>;
	sendAmountDetails: {
		fxRate?: string; // Optional - may be missing
		amount: {
			value: number;
			currencyCode: string;
		};
		fees: {
			value: number;
			currencyCode: string;
		};
		taxes: {
			value: number;
			currencyCode: string;
		};
		totalAmount: {
			value: number;
			currencyCode: string;
		};
		partnerFees?: {
			value: number;
			currencyCode: string;
		}; // Optional - v3 may use fees instead
	};
	payoutAmountDetails: {
		fxRate?: number; // Optional - may be missing
		fxRateEstimated: boolean;
		amount: {
			value: number;
			currencyCode: string;
		};
		fees?: {
			value: number;
			currencyCode: string;
		}; // Optional
		taxes?: {
			value: number;
			currencyCode: string;
		}; // Optional
		totalAmount: {
			value: number;
			currencyCode: string;
		};
		partnerFees?: {
			value?: number;
			currencyCode: string;
		}; // Optional
	};
	kind: "deposit" | "withdrawal" | "DEPOSIT" | "WITHDRAWAL" | string; // Handle both cases
}

// Types for the transaction update API
export interface TransactionUpdateRequest {
	targetAudience: "CONSUMER_FACING" | "AGENT_FACING";
	userLanguage: string;
	destinationCountryCode: string;
	destinationCountrySubdivisionCode?: string;
	payoutOptionCode: string;
	sendAmount: {
		value: number;
		currencyCode: string;
	};
	payoutCurrencyCode: string;
	sender: {
		firstName: string;
		lastName: string;
		middleName?: string;
		secondLastName?: string;
		notificationLanguagePreference: string;
		address: {
			line1: string;
			line2?: string;
			city: string;
			countrySubdivisionCode: string;
			countryCode: string;
			postalCode: string;
		};
		dateOfBirth: string;
		mobilePhone: {
			number: string;
			countryDialCode: string;
		};
		primaryIdentification: {
			typeCode: string;
			id: string;
			issueCountryCode: string;
			issueCountrySubdivisionCode?: string;
		};
	};
	additionalTransactionDetails: {
		getRelationshipToReceiver: boolean;
	};
	receiverSameAsSender: boolean;
}

export interface TransactionUpdateResponse {
	transactionId: string;
	cashInWalletFeeEnabled: boolean;
	cashOutWalletFeeEnabled: boolean;
	isRedirectRequired: boolean;
	readyForCommit: boolean;
	sendAmountDetails: {
		promotionDetails: Array<{
			code: string;
			discount: {
				value: number;
				currencyCode: string;
			};
		}>;
		fxRate: string;
		amount: {
			value: number;
			currencyCode: string;
		};
		fees: {
			value: number;
			currencyCode: string;
		};
		taxes: {
			value: number;
			currencyCode: string;
		};
		totalAmount: {
			value: number;
			currencyCode: string;
		};
		partnerFees: {
			value: number;
			currencyCode: string;
		};
	};
	payoutAmountDetails: {
		fxRate: number;
		fxRateEstimated: boolean;
		amount: {
			value: number;
			currencyCode: string;
		};
		fees: {
			value: number;
			currencyCode: string;
		};
		taxes: {
			value: number;
			currencyCode: string;
		};
		totalAmount: {
			value: number;
			currencyCode: string;
		};
	};
}

// Refund request/response types
export interface RefundInfoResponse {
	refundAmount: {
		value: number;
		currencyCode: string;
	};
	refundFee: {
		value: number;
		currencyCode: string;
	};
	totalRefundAmount: {
		value: number;
		currencyCode: string;
	};
}

export interface RefundCommitRequest {
	userLanguage: string;
	targetAudience: "CONSUMER_FACING" | "AGENT_FACING";
	refundReasonCode:
		| "NO_RCV_LOC"
		| "INCORRECT_AMT"
		| "CHANGE_MIND"
		| "DUP_TRAN"
		| "WRONG_CRNCY"
		| "WRONG_CNTRY"
		| "WRONG_STATE"
		| "TECH_PROB"
		| "NO_PICKUP"
		| "PROB_AT_LOC";
	sendAmount: {
		currencyCode: string;
		value: number;
	};
}

export interface RefundCommitResponse {
	refundId: string;
	status: string;
	refundAmount: {
		value: number;
		currencyCode: string;
	};
}

/**
 * V3 Prepare endpoint response
 * Returns transaction context that was previously passed via URL parameters
 */
export interface PrepareResponse {
	operation: string;
	lang: string;
	assetCode: string;
	transactionId: string;
	walletProvider: string;
	amount?: string; // Optional - when provided, should lock the amount input
}

export class TransactionService {
	constructor(private readonly httpClient: AxiosInstance) {}

	/**
	 * Prepare transaction - V3 API only
	 * Fetches transaction context (operation, lang, assetCode, transactionId, walletProvider)
	 * This replaces the URL parameters used in v2/v5 flows
	 */
	async prepare(config?: RequestConfig): Promise<PrepareResponse> {
		try {
			const response = await this.httpClient.post(
				"/transactions/prepare",
				{},
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
			return response.data as PrepareResponse;
		} catch (error) {
			throw this.handleApiError(error, "Failed to prepare transaction context");
		}
	}

	/**
	 * Prepare status - V3 API only
	 * Called before GET status for v3 API standalone status page
	 * Prepares the status context for fetching transaction status
	 */
	async prepareStatus(config?: RequestConfig): Promise<PrepareResponse> {
		try {
			const response = await this.httpClient.post(
				"/transactions/preparestatus",
				{},
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
			return response.data as PrepareResponse;
		} catch (error) {
			throw this.handleApiError(
				error,
				"Failed to prepare transaction status context",
			);
		}
	}

	/**
	 * Get transaction status by ID
	 * @param transactionId - The transaction ID
	 * @param refNumber - Optional reference number (v5 only - v3 doesn't require it)
	 * @param token - Auth token
	 * @param config - Optional request config
	 */
	async getTransactionStatus(
		transactionId: string,
		refNumber: string | undefined,
		token: string,
		config?: RequestConfig,
	): Promise<TransactionStatusResponse> {
		try {
			const response = await this.httpClient.get(
				`/transactions/${transactionId}`,
				{
					// Only include refNumber param if provided (v5 requires it, v3 doesn't)
					params: refNumber ? { refNumber } : undefined,
					headers: { Authorization: `Bearer ${token}` },
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
			return response.data as TransactionStatusResponse;
		} catch (error) {
			// Handle transaction status error
			throw this.handleApiError(error, "Failed to fetch transaction status");
		}
	}

	/**
	 * Get refund information for a transaction
	 */
	async getRefundInfo(
		transactionId: string,
		refundReasonCode: string,
		token: string,
		config?: RequestConfig,
	): Promise<RefundInfoResponse> {
		try {
			const response = await this.httpClient.get(
				`/refund/transactions/${transactionId}`,
				{
					params: { refundReasonCode },
					headers: { Authorization: `Bearer ${token}` },
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
			return response.data as RefundInfoResponse;
		} catch (error) {
			// Handle refund info error
			throw this.handleApiError(error, "Failed to fetch refund information");
		}
	}

	/**
	 * Commit refund for a transaction
	 */
	async commitRefund(
		transactionId: string,
		request: RefundCommitRequest,
		token: string,
		config?: RequestConfig,
	): Promise<RefundCommitResponse> {
		try {
			const response = await this.httpClient.put(
				`/refund/transactions/${transactionId}/commit`,
				request,
				{
					headers: { Authorization: `Bearer ${token}` },
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
			return response.data as RefundCommitResponse;
		} catch (error) {
			// Handle refund commit error
			throw this.handleApiError(error, "Failed to commit refund");
		}
	}

	/**
	 * Update transaction with user details
	 */
	async updateTransaction(
		transactionId: string,
		request: TransactionUpdateRequest,
		_config?: RequestConfig,
	): Promise<TransactionUpdateResponse> {
		try {
			// Validate required parameters
			this.validateUpdateRequest(transactionId, request);

			// Real API call - remove mocks for production testing

			const response = await this.httpClient.put(
				`/transactions/${transactionId}`,
				request,
				{
					params: {
						operation: _config?.params?.operation || "withdraw",
						..._config?.params,
					},
					signal: _config?.signal,
					timeout: _config?.timeout,
					..._config,
				},
			);

			return this.transformUpdateResponse(response.data);
		} catch (error) {
			// Handle transaction update error
			throw this.handleApiError(error, "Failed to update transaction");
		}
	}

	/**
	 * Validate update request requirements
	 */
	private validateUpdateRequest(
		transactionId: string,
		request: TransactionUpdateRequest,
	): void {
		if (!transactionId || typeof transactionId !== "string") {
			throw new Error("Transaction ID is required and must be a string");
		}

		if (!request) {
			throw new Error("Request payload is required");
		}

		if (!request.sender) {
			throw new Error("Sender information is required");
		}

		if (!request.sender.firstName || !request.sender.lastName) {
			throw new Error("Sender first name and last name are required");
		}

		if (!request.sender.address) {
			throw new Error("Sender address is required");
		}

		if (!request.sender.mobilePhone) {
			throw new Error("Sender mobile phone is required");
		}

		if (!request.sender.primaryIdentification) {
			throw new Error("Sender primary identification is required");
		}
	}

	/**
	 * Transform update API response
	 */
	private transformUpdateResponse(apiData: unknown): TransactionUpdateResponse {
		const responseData = apiData as Record<string, unknown>;

		if (!responseData || typeof responseData !== "object") {
			throw new Error("Invalid transaction update response format");
		}

		// Validate required fields in response
		if (!responseData.transactionId) {
			throw new Error("Missing transactionId in response");
		}

		if (!responseData.sendAmountDetails) {
			throw new Error("Missing sendAmountDetails in response");
		}

		if (!responseData.payoutAmountDetails) {
			throw new Error("Missing payoutAmountDetails in response");
		}

		// Transform to TransactionUpdateResponse structure
		return responseData as unknown as TransactionUpdateResponse;
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return new Error(`${defaultMessage}: ${error.message}`);
		}

		// Handle axios errors specifically
		if (typeof error === "object" && error && "response" in error) {
			const axiosError = error as {
				response: { data?: { message?: string }; status?: number };
			};
			const status = axiosError.response?.status;
			const message = axiosError.response?.data?.message || defaultMessage;
			return new Error(`${message} (Status: ${status})`);
		}

		// Handle network errors
		if (typeof error === "object" && error && "code" in error) {
			const networkError = error as { code: string };
			return new Error(
				`${defaultMessage}: Network error (${networkError.code})`,
			);
		}

		return new Error(defaultMessage);
	}
}
